import React, { useCallback, useContext, useEffect, useState } from 'react';
import Grid from './Grid';
import {
  GetContextMenuItemsParams,
  GridOptions,
  GridReadyEvent,
  IServerSideDatasource,
  RowClickedEvent,
} from 'ag-grid-community';
import buildGridOptions from './buildGridOptions';
import useGridFilterInput from './useGridFilterInput';
import useGridTotalsRow from './useGridTotalsRow';

import GridContext from '../../contexts/GridContext';
import NoDataToDisplay from '../V5Gadget/NoDataToDisplay';
import usePopup from '../../hooks/usePopup';
import DetailsSlideOutContext from '../../contexts/DetailsSlideOutContext';
import CustomerLaneSlideOutContext from '../../contexts/CustomerLaneSlideOut';
import CommitmentQueryContext from '../../contexts/CommitmentQueryContext';
import useDatasource from '../../hooks/grids/useDatasource';
import ServerSideDataSource from './ServerSideDatasource';
import GridDataSourceContext from '../../contexts/GridDataSourceContext';
import BaseViewsContext from '../../contexts/BaseViewsContext';
import useColumnDefs from '../../hooks/useColumnDefs';
import GqlClientContext from '../../contexts/GqlClientContext';
import useDateScope from '../../hooks/useDateScope';
import { RowHeightContext } from '../../contextProviders/SplashScreenProviders/MiscProviders/RowHeightProvider';

const GridContainerGate = ({
  bottomBar,
  isBoard,
  onRowClicked,
  getContextMenuItems,
  hideTotalsRow,
  disableAutoLayout,
  layoutOnColumnChange,
  layoutOnFirstRender,
  layoutOnModelUpdated,
  autoSizeFirstColumnOnly,
  autoSizeSecondColumnOnly,
  autoSizeSkipHeader,
  hasBorders,
  hasDarkHeader,
  isSearchScreen,
}: {
  bottomBar?: JSX.Element | JSX.Element[];
  isBoard?: boolean;
  onRowClicked?: (event: RowClickedEvent) => void;
  getContextMenuItems?: (params: GetContextMenuItemsParams) => {
    name: string;
    action: () => void;
  }[];
  isPopupGrid?: boolean;
  hideTotalsRow?: boolean;
  disableAutoLayout?: boolean;
  layoutOnModelUpdated?: boolean;
  layoutOnFirstRender?: boolean;
  layoutOnColumnChange?: boolean;
  autoSizeFirstColumnOnly?: boolean;
  autoSizeSecondColumnOnly?: boolean;
  autoSizeSkipHeader?: boolean;
  hasBorders: boolean;
  hasDarkHeader: boolean;
  isSearchScreen?: boolean;
}) => {
  const { baseViews } = useContext(BaseViewsContext);
  const {
    dataType,
    gridQueryOverride,
    gridTotalsQueryOverride,
    gridCountQueryOverride,
    baseViewOverride,
    sortOverride,
    gridId,
  } = useContext(GridContext);

  const baseView = baseViewOverride ? baseViewOverride : baseViews[dataType];

  if (baseView === undefined) {
    return <NoDataToDisplay size={'normal'} />;
  }

  return (
    <GridContainer
      key={gridId}
      baseView={baseView}
      bottomBar={bottomBar}
      gridQueryOverride={gridQueryOverride}
      gridCountQueryOverride={gridCountQueryOverride}
      gridTotalsQueryOverride={gridTotalsQueryOverride}
      isBoard={isBoard}
      sort={sortOverride}
      onRowClicked={onRowClicked}
      getContextMenuItems={getContextMenuItems}
      hideTotalsRow={hideTotalsRow}
      disableAutoLayout={disableAutoLayout}
      layoutOnModelUpdated={layoutOnModelUpdated}
      layoutOnFirstRender={layoutOnFirstRender}
      layoutOnColumnChange={layoutOnColumnChange}
      autoSizeFirstColumnOnly={autoSizeFirstColumnOnly}
      autoSizeSecondColumnOnly={autoSizeSecondColumnOnly}
      autoSizeSkipHeader={autoSizeSkipHeader}
      hasBorders={hasBorders}
      hasDarkHeader={hasDarkHeader}
      isSearchScreen={isSearchScreen}
    />
  );
};

const useShouldUseNewDataSource = (
  gridQueryOverride?: string,
  currentGroup?: string,
) => {
  const getShouldUseNewDataSource = useCallback(() => {
    const isFetchRecords = !gridQueryOverride;
    return isFetchRecords && !currentGroup;
  }, [currentGroup, gridQueryOverride]);
  const [shouldUseNewDataSource, setShouldUseNewDataSource] = useState<boolean>(
    getShouldUseNewDataSource(),
  );
  useEffect(() => {
    setShouldUseNewDataSource(getShouldUseNewDataSource());
  }, [getShouldUseNewDataSource]);

  return shouldUseNewDataSource;
};

export const useGridDataSource = ({
  gridQueryOverride,
  gridCountQueryOverride,
  baseView,
  sort,
}: {
  gridQueryOverride?: string;
  gridCountQueryOverride?: string;
  baseView: FleetOps.BaseView;
  sort?: SimpleGridSort[];
}) => {
  const { dataType } = useContext(GridContext);
  const { client } = useContext(GqlClientContext);
  const { includeFields, filterInput } = useGridFilterInput({ baseView });
  const shouldUseNewDataSource = useShouldUseNewDataSource(gridQueryOverride);
  const dateScope = useDateScope({});

  const { dataSource: paginationDataSource } = useDatasource(
    includeFields,
    dataType,
    filterInput,
  );
  const [dataSource, setDataSource] = useState<
    IServerSideDatasource | DataSource
  >();

  const getDataSource = useCallback(() => {
    if (shouldUseNewDataSource) {
      return paginationDataSource;
    } else {
      return ServerSideDataSource({
        client,
        filterInput,
        dateScope,
        baseView,
        gridQueryOverride,
        gridCountQueryOverride,
        sort,
      });
    }
  }, [
    baseView,
    client,
    dateScope,
    filterInput,
    gridCountQueryOverride,
    gridQueryOverride,
    paginationDataSource,
    shouldUseNewDataSource,
    sort,
  ]);

  useEffect(() => {
    setDataSource(getDataSource());
  }, [getDataSource]);

  return { dataSource, dataSourceFilterInput: filterInput };
};

const GridContainer = ({
  bottomBar,
  baseView,
  gridQueryOverride,
  gridCountQueryOverride,
  gridTotalsQueryOverride,
  isBoard,
  sort,
  onRowClicked,
  getContextMenuItems,
  hideTotalsRow,
  disableAutoLayout,
  layoutOnColumnChange,
  layoutOnFirstRender,
  layoutOnModelUpdated,
  autoSizeFirstColumnOnly,
  autoSizeSecondColumnOnly,
  autoSizeSkipHeader,
  hasBorders,
  hasDarkHeader,
  isSearchScreen,
}: {
  bottomBar?: JSX.Element | JSX.Element[];
  baseView: FleetOps.BaseView;
  gridQueryOverride?: string;
  gridCountQueryOverride?: string;
  gridTotalsQueryOverride?: string;
  isBoard?: boolean;
  sort?: SimpleGridSort[];
  onRowClicked?: (event: RowClickedEvent) => void;
  getContextMenuItems?: (params: GetContextMenuItemsParams) => {
    name: string;
    action: () => void;
  }[];
  hideTotalsRow?: boolean;
  disableAutoLayout?: boolean;
  layoutOnModelUpdated?: boolean;
  layoutOnFirstRender?: boolean;
  layoutOnColumnChange?: boolean;
  autoSizeFirstColumnOnly?: boolean;
  autoSizeSecondColumnOnly?: boolean;
  autoSizeSkipHeader?: boolean;
  hasBorders: boolean;
  hasDarkHeader: boolean;
  isSearchScreen?: boolean;
}) => {
  // Context
  const { rowHeight } = useContext(RowHeightContext);
  const { isCustomerLaneSlideOpen } = useContext(CustomerLaneSlideOutContext);
  const { isSlideOutOpen: isNewCommitmentSlideOpen } = useContext(
    CommitmentQueryContext,
  );
  const { gridId, setGridApi, gridApi } = useContext(GridContext);

  // State
  const [selectedOrderNumber, setSelectedOrderNumber] = useState<
    string | undefined
  >();
  const [gridOptions, setGridOptions] = useState<GridOptions>();

  // Hooks
  const columnDefs = useColumnDefs(
    baseView,
    undefined,
    sort,
    disableAutoLayout || autoSizeFirstColumnOnly || autoSizeSecondColumnOnly,
  );

  const {
    isOpen: isDetailsSlideOpen,
    open: openDetailsSlide,
    close: closeDetailsSlide,
  } = usePopup();

  // DATA SOURCE
  const { dataSource, dataSourceFilterInput } = useGridDataSource({
    baseView,
    gridQueryOverride,
    gridCountQueryOverride,
    sort,
  });

  useEffect(() => {
    if (!dataSource) {
      return;
    }

    const newGridOptions = buildGridOptions({
      columnDefs,
      rowModelType: 'serverSide',
      suppressColumnVirtualisation: true,
      rowHeight,
    });

    setGridOptions({
      ...newGridOptions,
      popupParent: document.querySelector('body'),
      getContextMenuItems,
      serverSideDatasource: dataSource,
    });
  }, [columnDefs, dataSource, getContextMenuItems, rowHeight]);

  const onGridReady = useCallback(
    (event: GridReadyEvent) => {
      setGridApi(event.api);
    },
    [setGridApi],
  );

  useEffect(() => {
    if (gridApi && dataSource) {
      gridApi.setGridOption('serverSideDatasource', dataSource);
    }
  }, [dataSource, gridApi]);

  const totalsRow = useGridTotalsRow({
    baseView,
    filterInput: dataSourceFilterInput,
    gridTotalsQueryOverride,
    hideTotalsRow,
  });

  if (!gridOptions) {
    return null;
  }

  return (
    <div style={{ height: '100%' }}>
      <DetailsSlideOutContext.Provider
        value={{
          selectedOrderNumber,
          setSelectedOrderNumber,
          isDetailsSlideOpen,
          openDetailsSlide,
          closeDetailsSlide,
        }}
      >
        <GridDataSourceContext.Provider value={{ dataSource }}>
          <Grid
            columnDefs={columnDefs}
            onGridReady={onGridReady}
            gridOptions={gridOptions}
            totalsRow={totalsRow}
            gridId={gridId}
            bottomBar={bottomBar}
            isDetailsSlideOpen={isDetailsSlideOpen}
            isCustomerLaneSlideOpen={isCustomerLaneSlideOpen}
            isNewCommitmentSlideOpen={isNewCommitmentSlideOpen}
            isBoard={isBoard}
            onRowClicked={onRowClicked}
            disableAutoLayout={disableAutoLayout}
            layoutOnColumnChange={layoutOnColumnChange}
            layoutOnFirstRender={layoutOnFirstRender}
            layoutOnModelUpdated={layoutOnModelUpdated}
            autoSizeFirstColumnOnly={autoSizeFirstColumnOnly}
            autoSizeSecondColumnOnly={autoSizeSecondColumnOnly}
            autoSizeSkipHeader={autoSizeSkipHeader}
            hasBorders={hasBorders}
            hasDarkHeader={hasDarkHeader}
            isSearchScreen={isSearchScreen}
          />
        </GridDataSourceContext.Provider>
      </DetailsSlideOutContext.Provider>
    </div>
  );
};

export default GridContainerGate;
